/* assets/cart-recommendations.js
   Cart drawer recommendations carousel (related products)
   - Fetches related products from Shopify recommendations endpoint (Search & Discovery related logic)
   - 1 visible at a time, 8–10 total
   - Prev/Next arrows + swipe/drag
   - AJAX add to cart (no redirect)
   - Forces real-time cart drawer refresh + cart bubble refresh (no page reload)
*/

// @ts-nocheck
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

(() => {
  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this._index = 0;
      this._items = [];
      this._loadedFor = null;
      this._lastSeedFetchAt = 0;
      this._observer = null;

      this._onClick = this._onClick.bind(this);
      this._onCartUpdate = this._onCartUpdate.bind(this);
    }

    // Always query fresh so we never hold a stale reference after morphSection
    get track() {
      return this.querySelector('[data-cd-track]');
    }

    get viewport() {
      return this.querySelector('[data-cd-viewport]');
    }

    connectedCallback() {
      if (!this.querySelector('[data-cd-viewport]') || !this.querySelector('[data-cd-track]')) return;

      this.prevBtn = this.querySelector('[data-cd-prev]');
      this.nextBtn = this.querySelector('[data-cd-next]');

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this._index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this._index + 1));

      this.addEventListener('click', this._onClick);
      this._bindSwipe();

      // Reset cache so load() always runs on (re-)connect
      this._loadedFor = null;

      // Watch for section morph/hydration clearing the track and re-populate from cache
      this._setupObserver();

      this.load();

      document.addEventListener(ThemeEvents.cartUpdate, this._onCartUpdate);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      document.removeEventListener(ThemeEvents.cartUpdate, this._onCartUpdate);
      this._observer?.disconnect();
      this._observer = null;
    }

    _setupObserver() {
      this._observer?.disconnect();

      this._observer = new MutationObserver(() => {
        const track = this.track;
        if (!track) return;

        // If track was emptied by morph but we have cached items, re-render immediately
        if (track.children.length === 0 && this._items.length > 0) {
          // Pause observer to prevent re-entrancy while we write innerHTML
          this._observer.disconnect();

          track.innerHTML = this._items.map((p) => this._renderSlide(p)).join('');

          const safeIndex = Math.min(this._index, this._items.length - 1);
          track.style.transform = `translateX(-${safeIndex * 100}%)`;
          this._updateNav();

          // Re-enable observer after the current microtask settles
          Promise.resolve().then(() => {
            if (this._observer) {
              this._observer.observe(this, { childList: true, subtree: true });
            }
          });
        }
      });

      this._observer.observe(this, { childList: true, subtree: true });
    }

    async _onCartUpdate(event) {
      const data = event?.detail?.data || {};

      // If we dispatched an event ourselves, we get the seed product id from the payload.
      const productIdFromEvent = data.productId ?? data.product_id;
      if (productIdFromEvent) {
        const nextSeed = String(productIdFromEvent);
        if (nextSeed && nextSeed !== this.dataset.productId) {
          this.dataset.productId = nextSeed;
          await this.load(true);
        }
        return;
      }

      // Otherwise we don't always have cart line items in the event payload.
      // Fetch /cart.js to keep recommendations synced with the cart (throttled).
      const now = Date.now();
      if (now - this._lastSeedFetchAt < 900) return;
      this._lastSeedFetchAt = now;

      try {
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!cartRes.ok) return;
        const cartJson = await cartRes.json();
        const seed = cartJson?.items?.[0]?.product_id;

        if (!seed) return;
        const nextSeed = String(seed);

        if (nextSeed && nextSeed !== this.dataset.productId) {
          this.dataset.productId = nextSeed;
          await this.load(true);
        }
      } catch (_) {
        // non-critical
      }
    }

    async load(force = false) {
      const productId = this.dataset.productId;
      const limit = parseInt(this.dataset.limit || '10', 10);
      const fallbackCollectionHandle = this.dataset.fallbackCollectionHandle;

      if (!productId) return;

      if (!force && this._loadedFor === productId) return;
      this._loadedFor = productId;
      this._index = 0;

      try {
        const root = window.Shopify?.routes?.root || window.routes?.root || '/';
        const url =
          `${root}recommendations/products.json` +
          `?product_id=${encodeURIComponent(productId)}` +
          `&limit=${encodeURIComponent(limit)}` +
          `&intent=related`;

        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`Recommendations fetch failed: ${res.status}`);

        const data = await res.json();
        const products = data?.products || [];

        let finalProducts = products;

        // Fallback: if Shopify returns fewer than `limit`, optionally pull more products
        // from a manually selected collection.
        if (fallbackCollectionHandle && finalProducts.length < limit) {
          const remaining = Math.max(0, limit - finalProducts.length);
          if (remaining > 0) {
            try {
              const fallbackUrl = `${root}collections/${encodeURIComponent(fallbackCollectionHandle)}/products.json?limit=${encodeURIComponent(remaining)}`;
              const fallbackRes = await fetch(fallbackUrl, { credentials: 'same-origin' });
              if (fallbackRes.ok) {
                const fallbackData = await fallbackRes.json();
                const fallbackProducts = fallbackData?.products || [];

                const existingIds = new Set(finalProducts.map((p) => String(p?.id)));
                const merged = [
                  ...finalProducts,
                  ...fallbackProducts.filter((p) => p?.id != null && !existingIds.has(String(p.id))),
                ];

                finalProducts = merged.slice(0, limit);
              }
            } catch (_) {
              // Non-critical fallback
            }
          }
        }

        if (!finalProducts.length) {
          this.style.display = 'none';
          return;
        }

        this.style.display = '';
        this._items = finalProducts;

        this.track.innerHTML = finalProducts.map((p) => this._renderSlide(p)).join('');
        this.goTo(0);
        this._updateNav();
      } catch (e) {
        console.error('[cart-recommendations] load failed', e);
        this.style.display = 'none';
      }
    }

    _renderSlide(p) {
      try {
        const title = this._esc(p.title || 'Untitled Product');
        const url = p.url || (p.handle ? `/products/${p.handle}` : '#');
      
      // Handle different image formats
      let img = '';
      if (p.featured_image) {
        img = `<img src="${p.featured_image}" alt="${title}" loading="lazy">`;
      } else if (p.image && p.image.src) {
        img = `<img src="${p.image.src}" alt="${title}" loading="lazy">`;
      } else if (p.images && p.images.length > 0) {
        const imgSrc = typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src;
        img = `<img src="${imgSrc}" alt="${title}" loading="lazy">`;
      }
      
      // Handle different price formats
      const priceCents =
        typeof p.price === 'number'
          ? p.price
          : typeof p.price === 'string' && p.price.trim() !== '' && !isNaN(Number(p.price))
            ? Number(p.price)
            : p.price_min
            ? p.price_min
            : null;
      const price = priceCents != null ? this._formatMoney(priceCents) : '';
      
      const variantId = p?.variants?.[0]?.id;
      const productId = p?.id;
      const productIdAttr = productId ? ` data-product-id="${productId}"` : '';

      return `
        <div class="cd-rec__slide">
          <div class="cd-rec__item">
            <a class="cd-rec__img" href="${url}" aria-label="${title}">${img}</a>
            <div class="cd-rec__meta">
              <p class="cd-rec__name"><a href="${url}">${title}</a></p>
              <p class="cd-rec__price">${price}</p>
              ${
                variantId
                  ? `<button type="button" class="button button--primary cd-rec__add" data-cd-add data-variant-id="${variantId}"${productIdAttr}>
                       Add
                     </button>`
                  : ``
              }
            </div>
          </div>
        </div>
      `;
      } catch (e) {
        return `<div class="cd-rec__slide"><div class="cd-rec__item">Error rendering product</div></div>`;
      }
    }

    goTo(i) {
      const max = this._items.length - 1;
      if (max < 0) return;

      // Loop always
      if (i < 0) i = max;
      if (i > max) i = 0;

      this._index = i;
      this.track.style.transform = `translateX(-${this._index * 100}%)`;
      this._updateNav();
    }

    _updateNav() {
      const max = this._items.length - 1;
      const multi = max >= 1;

      if (this.prevBtn) this.prevBtn.disabled = !multi;
      if (this.nextBtn) this.nextBtn.disabled = !multi;
    }

    async _onClick(e) {
      const btn = e.target.closest('[data-cd-add]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      const productIdSeed = btn.getAttribute('data-product-id');
      if (productIdSeed) this.dataset.productId = String(productIdSeed);

      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      try {
        // 1) Add via AJAX (no redirect)
        await this._ajaxAddToCart(variantId, 1);
        // 2) Notify Dwell cart components (drawer + bubble) to refresh.
        await this._dispatchCartUpdate({ variantId, productIdSeed });

        // 3) Immediately reload recommendations for the product we added.
        if (productIdSeed) {
          await this.load(true);
        }

        btn.textContent = 'Added';
        setTimeout(() => (btn.textContent = original), 900);
      } catch (err) {
        console.error('[cart-recommendations] add failed', err);
        btn.textContent = 'Error';
        setTimeout(() => (btn.textContent = original), 1200);
      } finally {
        btn.disabled = false;
      }
    }

    async _ajaxAddToCart(variantId, qty) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: Number(variantId), quantity: Number(qty || 1) }),
      });

      if (!res.ok) {
        let msg = `Add failed (${res.status})`;
        try {
          const err = await res.json();
          if (err?.description) msg = err.description;
        } catch (_) {}
        throw new Error(msg);
      }

      return res.json();
    }

    async _dispatchCartUpdate({ variantId, productIdSeed }) {
      try {
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!cartRes.ok) return;
        const cartJson = await cartRes.json();
        const itemCount = cartJson?.item_count ?? 0;

        document.dispatchEvent(
          new CartAddEvent(cartJson, 'cart-recommendations', {
            source: 'cart-recommendations',
            itemCount,
            variantId,
            productId: productIdSeed,
          })
        );
      } catch (_) {
        // ignore
      }
    }

    _bindSwipe() {
      // Capture current viewport element for swipe binding;
      // if morph replaces it, swipe re-binds on next connectedCallback.
      const vp = this.viewport;
      if (!vp) return;

      let startX = 0;
      let currentX = 0;
      let dragging = false;

      const start = (x) => {
        dragging = true;
        startX = x;
        currentX = x;
      };

      const move = (x) => {
        if (!dragging) return;
        currentX = x;
      };

      const end = () => {
        if (!dragging) return;
        dragging = false;

        const dx = currentX - startX;
        if (Math.abs(dx) < 35) return;
        if (dx < 0) this.goTo(this._index + 1);
        else this.goTo(this._index - 1);
      };

      vp.addEventListener('touchstart', (e) => start(e.touches[0].clientX), { passive: true });
      vp.addEventListener('touchmove', (e) => move(e.touches[0].clientX), { passive: true });
      vp.addEventListener('touchend', end);

      vp.addEventListener('mousedown', (e) => {
        e.preventDefault();
        start(e.clientX);
      });
      window.addEventListener('mousemove', (e) => move(e.clientX));
      window.addEventListener('mouseup', end);
    }

    _formatMoney(cents) {
      try {
        const moneyFormat = this.dataset?.moneyFormat || '{{amount}}';
        const currency = this.dataset?.currency || window.theme?.currency || 'USD';
        return formatMoney(Number(cents), moneyFormat, currency);
      } catch (e) {
        return '$' + (Number(cents) / 100).toFixed(2);
      }
    }

    _esc(str) {
      return String(str || '').replace(/[&<>"']/g, (m) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[m]));
    }
  }

  if (!customElements.get('cart-recommendations')) {
    customElements.define('cart-recommendations', CartRecommendations);
  }
})();